import { useCallback, useEffect, useMemo, useState } from "react";

import { roleHasPermission } from "@blis/shared/permissions";
import { type RouterOutputs, trpc } from "@blis/trpc/react";

import { useAuth } from "@/features/auth/hooks";
import { isElectron } from "../electron";
import { getElectronConfigSafe } from "../electron-config";
import { setSelectedPrinter, toPrinterTarget } from "./electron-print";

type Printer = RouterOutputs["printers"]["list"][number];

export function useWorkstationPrinter() {
  const { user } = useAuth();
  const enabled = isElectron() && roleHasPermission(user.role, "read:printers");
  const { data: printers } = trpc.printers.list.useQuery(undefined, {
    enabled,
  });
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      setSelectedId(null);
      setSelectedPrinter(null);
      return;
    }

    let cancelled = false;
    void (async () => {
      const config = await getElectronConfigSafe();
      if (cancelled) return;

      setSelectedId(config?.selectedPrinterId ?? null);
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const selectedPrinter = useMemo(() => {
    if (!selectedId) return null;
    return (
      printers?.find(
        (printer) => printer.id === selectedId && printer.enabled,
      ) ?? null
    );
  }, [printers, selectedId]);

  useEffect(() => {
    if (!enabled) return;

    if (!selectedId) {
      setSelectedPrinter(null);
      return;
    }

    if (!printers) return;

    setSelectedPrinter(
      selectedPrinter ? toPrinterTarget(selectedPrinter) : null,
    );
  }, [enabled, printers, selectedId, selectedPrinter]);

  const selectPrinter = useCallback(
    (id: number | null) => {
      setSelectedId(id);

      const match = printers?.find(
        (printer) => printer.id === id && printer.enabled,
      );
      setSelectedPrinter(match ? toPrinterTarget(match) : null);
    },
    [printers],
  );

  return {
    printers,
    selectedId,
    selectedPrinter,
    setSelectedId: selectPrinter,
  };
}

export function useElectronPrinter() {
  useWorkstationPrinter();
}
